import { TimetableRuleState } from '@chirp/access-verification';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import {
  PublicAccessPointRoles,
  usegetAccessPointQuery,
  useunlockAccessPointMutation,
} from '../../apollo';
import {
  getAvailabilityDescription,
  getAvailabilityState,
  getErrorMessage,
  isDateAllowedOnTimetable,
} from '../../helpers';
import AlertModal from '../AlertModal';
import UnlockDial from '../UnlockDial';
import animationData from '../UnlockDial/unlocking-animation-white.json';

interface QRScannerDialProps {
  accessPointId: string;
  expiresAt?: string | null;
  onUnlockError?: (error: any) => void;
  onClose: () => void;
  roleKey: keyof PublicAccessPointRoles;
}

const QRScannerDial: React.FC<QRScannerDialProps> = (props) => {
  const [isRestricted, setIsRestricted] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [availability, setAvailability] = useState('');
  const [hasAccess, setHasAccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { accessPointId, expiresAt, onClose, roleKey, onUnlockError } = props;

  const [unlockAccessPoint] = useunlockAccessPointMutation({
    variables: { accessPointId },
  });

  const { loading, data } = usegetAccessPointQuery({
    skip: !accessPointId,
    variables: { accessPointId },
    onCompleted: async ({ accessPoint: loadedAccessPoint }) => {
      try {
        const { schedule } = loadedAccessPoint.publicAccessPointRoles[roleKey];
        const { timezone } = loadedAccessPoint.property;

        const accessTime = new Date();

        if (expiresAt && !moment(accessTime).isBefore(expiresAt)) {
          // Simulate API error when access is expired
          throw new Error('You do not have access to this entry point');
        }

        const isAccessPointAvailable = isDateAllowedOnTimetable(accessTime, schedule, timezone);
        const availabilityState = getAvailabilityState(new Date(), schedule, timezone);

        if (
          !isAccessPointAvailable ||
          (isAccessPointAvailable && availabilityState === TimetableRuleState.UNLOCKED)
        ) {
          setTimeout(() => {
            onClose();
          }, 5000);
        } else {
          await unlockAccessPoint();
          setIsRestricted(false);
          setIsAvailable(true);
          setIsUnlocking(true);
        }

        setHasAccess(true);
        setAvailability(getAvailabilityDescription(accessTime, schedule, timezone));
      } catch (error) {
        if (typeof onUnlockError === 'function') {
          onUnlockError(error);
          onClose();
        } else {
          setIsRestricted(true);
          setHasAccess(false);
          setErrorMessage(getErrorMessage((error as any), 'Unable to unlock entry point'));
        }
      }
    },
    onError: (error) => {
      setErrorMessage(getErrorMessage(error, 'Unable to unlock entry point'));
    },
  });

  const accessPoint = data ? data.accessPoint : null;

  if (errorMessage) {
    return (
      <AlertModal
        isOpen
        title="Unable to unlock entry point"
        content={errorMessage}
        closeModal={() => {
          setErrorMessage(null);
          onClose();
        }}
      />
    );
  }

  if (loading) {
    return null;
  }

  if (accessPoint) {
    const { name, property } = accessPoint;
    const { schedule } = accessPoint.publicAccessPointRoles[roleKey];
    const { timezone } = property;

    const accessTimeout = accessPoint.accessTimeout || 5000;
    const availabilityState = getAvailabilityState(new Date(), schedule, timezone);

    return (
      <div className="qr-scanner-dial">
        <div className="qr-scanner-dial-wrapper disabled-button">
          <UnlockDial
            animationData={animationData}
            accessTimeout={accessTimeout}
            availability={availability}
            hasAccess={hasAccess}
            header={hasAccess ? name : ''}
            isAutomaticallyUnlocked={availabilityState === TimetableRuleState.UNLOCKED}
            isAvailable={isAvailable}
            isRestricted={isRestricted}
            isUnlocking={isUnlocking}
            onComplete={onClose}
          />
        </div>
      </div>
    );
  }

  return null;
};

export default QRScannerDial;
